import { isAssignmentSourceAllowedAtSlot, topicParticleOf } from '@safetyhub/shared';
import type { ApprovalLineMetaRow, AssignmentSource } from '@safetyhub/shared';
import AssignmentChainEditor, {
  SOURCE_TYPES,
  SOURCE_TYPE_LABELS,
  chainAssignableTypes,
  firstAllowedSourceType,
  makeDefaultSource,
} from './AssignmentChainEditor';
import { Badge } from '../Badge';

interface ApprovalLineMetaEditorProps {
  lines: ApprovalLineMetaRow[];
  onChange: (lines: ApprovalLineMetaRow[]) => void;
  disableMemberType?: boolean;
  /** 좁은 패널(Composer)용 compact 레이아웃 — 헤더 세로 적층 + locked full-width + 들여쓰기 축소 */
  compact?: boolean;
  /**
   * 새로 추가되는 칸의 `locked` 기본값. 기본 `true`(칸 고정).
   *
   * Composer(양식 저작)는 `false`를 준다 — 양식은 **칸의 뼈대**를 정의하고 현장·작성자가
   * 실제 운영에 맞게 손대는 것이 기본이라, 새 칸을 잠근 채 시작하면 저작자가 매번 풀어야 한다.
   * 현장 override 카드(`ApprovalLineMetaCard`)는 기본값 `true`를 쓴다.
   */
  newRowLocked?: boolean;
}

/**
 * 담당자 미지정 칸의 어휘 — **작성 페이지(web `EditModal`)와 같은 말**을 쓴다.
 * 저작자가 Composer에서 만든 칸을 작성자가 그대로 보게 되므로, 두 화면이 다른 단어로
 * 같은 상태를 부르면 "그 배지가 이 배지인가"를 매번 되짚게 된다.
 */
export const UNASSIGNED_BADGE_LABEL = '담당자 미지정';
const UNASSIGNED_HINT = '이 칸은 종이(수기)로 결재합니다. 출력물에 빈 서명란으로 남습니다.';

/**
 * 새 칸의 기본값 — **담당자 미지정(빈 `assignmentChain`)**.
 *
 * 결재란 추가와 담당자 지정은 별개 축이다(결정 2026-08-13). 저작(Composer/type 메타)은 **칸만**
 * 정의하고 담당자는 작성자가 보고서 단위로 채운다. 옛 기본값 `[{ type: 'delegatedTo' }]`는
 * "칸만 추가"를 아예 불가능하게 만들었고, 위임은 특수한 흐름이라 기본값으로도 부적절했다.
 *
 * 빈 체인은 서버 전 경로가 이미 "미지정 칸"으로 취급한다 — `isApprovalSlotUnassigned`,
 * `buildApprovalLineInstanceFromMeta`(→ `assigneeUnset`), `enforceLockedFromSystem`(하위가
 * 비운 것을 admin 값으로 되돌리지 않음). type/COMPOSED 구분이 없으므로 이 기본값은 세 소비처
 * (Composer 양식 탭 / 시스템 type 메타 카드 / type 메타 편집 모달) 모두에 그대로 맞다.
 */
function makeDefaultRow(level: number, locked: boolean): ApprovalLineMetaRow {
  return {
    level,
    label: `결재 ${level}단계`,
    locked,
    assignmentChain: [],
  };
}

/** 이 칸에 담당자가 지정되지 않았는가(= 오프라인 수기 결재 칸). */
function isUnassigned(row: ApprovalLineMetaRow): boolean {
  return (row.assignmentChain?.length ?? 0) === 0;
}

export default function ApprovalLineMetaEditor({
  lines,
  onChange,
  disableMemberType,
  compact = false,
  newRowLocked = true,
}: ApprovalLineMetaEditorProps) {
  const addRow = () => {
    const nextLevel = lines.length > 0 ? Math.max(...lines.map((l) => l.level)) + 1 : 1;
    onChange([...lines, makeDefaultRow(nextLevel, newRowLocked)]);
  };

  const removeRow = (idx: number) => {
    onChange(lines.filter((_, i) => i !== idx));
  };

  const updateLabel = (idx: number, label: string) => {
    const next = [...lines];
    next[idx] = { ...next[idx], label };
    onChange(next);
  };

  const updateLocked = (idx: number, locked: boolean) => {
    const next = [...lines];
    next[idx] = { ...next[idx], locked };
    onChange(next);
  };

  const updateChain = (idx: number, chain: AssignmentSource[]) => {
    const next = [...lines];
    next[idx] = { ...next[idx], assignmentChain: chain };
    onChange(next);
  };

  /**
   * 미지정 → 지정 전환. whitelist가 있으면 **허용된 첫 소스**로 시작한다 — 허용되지 않는 값을
   * 기본으로 넣으면 서버(`enforceLockedFromSystem`)가 그 값만 거부해 "저장했는데 되돌아온다"가
   * 된다. 제한이 없으면 종전 기본값(위임 대상)을 그대로 쓴다.
   */
  const assignAssignee = (idx: number) => {
    // S77: 첫 칸이 아니면 `작성자`·`위임 대상`은 후보에서 빠진다 — 서버가 거부할 값을
    // 기본으로 넣으면 "저장했는데 400"이 된다.
    const type = firstAllowedSourceType(
      lines[idx].assigneeSourceWhitelist,
      disableMemberType,
      idx,
    );
    if (!type) return;
    updateChain(idx, [makeDefaultSource(type)]);
  };

  /** 지정 → 미지정 전환. 칸은 그대로 두고 담당자만 뗀다(칸 삭제와 다른 축이다). */
  const unsetAssignee = (idx: number) => {
    updateChain(idx, []);
  };

  /**
   * `assigneeSourceWhitelist` 토글. **선택 0개 = 제한 없음**이므로 필드 자체를 지운다.
   *
   * 빈 배열을 남기면 안 된다 — 서버 `isAssignmentChainAllowed`는 `!whitelist`만 "제한 없음"으로
   * 보고 `[]`는 통과시키지 않아, **어떤 담당자도 지정할 수 없는 칸**이 된다. 기존 템플릿에
   * 빈 배열이 박히는 것도 같은 이유로 막아야 한다.
   */
  const toggleWhitelist = (idx: number, type: AssignmentSource['type']) => {
    const current = lines[idx].assigneeSourceWhitelist;
    const nextList = current?.includes(type)
      ? current.filter((t) => t !== type)
      : [...(current ?? []), type];

    const nextRow: ApprovalLineMetaRow = { ...lines[idx] };
    if (nextList.length > 0) nextRow.assigneeSourceWhitelist = nextList;
    else delete nextRow.assigneeSourceWhitelist;

    const next = [...lines];
    next[idx] = nextRow;
    onChange(next);
  };

  /**
   * 담당자 지정은 **앞에서부터 연속(prefix)** 이어야 한다(결정 2026-08-13 §2). 첫 미지정 칸의
   * index — 그 뒤에 담당자가 지정된 칸이 있으면 저작 시점에 경고한다.
   *
   * ⚠️ 서버 prefix 검증(`assertApprovalSlotPrefix`)은 **템플릿/type 메타 저장에는 걸리지 않는다**
   * — 보고서 결재라인 정의 저장(`setReportApprovalLines`)과 제출(`report-approval.service`)
   * 두 곳뿐이다. 그래서 여기서 막지 않으면 위반이 기본값으로 박히고, 저작자는 모른 채
   * **작성자가 제출에서 400으로 막힌다.** 저작 중간 상태(뒤 칸부터 채우는 중)를 하드 블록하면
   * 편집 자체가 불가능해지므로 경고만 한다.
   */
  const firstUnassignedIndex = lines.findIndex(isUnassigned);

  return (
    <div className="space-y-3">
      {lines.map((row, idx) => {
        const unassigned = isUnassigned(row);
        const prefixViolating =
          !unassigned && firstUnassignedIndex >= 0 && idx > firstUnassignedIndex;
        const whitelist = row.assigneeSourceWhitelist;
        /**
         * 이 칸의 기본 체인이 자기 whitelist를 벗어났는가 — 제한을 **나중에 좁히면** 생긴다.
         * select는 허용 밖 값을 비활성으로만 두므로(선택 유지) 화면이 조용해질 수 있어 명시한다.
         */
        const whitelistViolating =
          !unassigned && !!whitelist && row.assignmentChain.some((s) => !whitelist.includes(s.type));
        /**
         * 이 칸에 **첫 결재란 전용 소스**(`작성자`·`위임 대상`)가 이미 들어 있는가 — S77 위반
         * (결정 `2026-08-27-author-delegated-sources-first-slot-only.md`).
         *
         * `canAssignHere`와 **축이 다르다** — 저건 「여기에 *새로* 세울 수 있나」(선택지 제한)이고
         * 이건 「*이미 들어 있는* 값이 위반인가」다. 아래 소스 select는 허용 밖 값을 `&& !checked`로
         * 비활성에서 빼 **선택을 유지**하므로(종전 값 보존) 위반이 화면에서 조용해진다 —
         * `whitelistViolating`과 같은 기제이고, 그래서 같은 형태로 명시한다.
         *
         * 명시가 없으면 저작자는 **저장을 눌러야** 서버 400으로 알게 된다. 저장 경로 양쪽이 거부한다:
         * 양식 메타는 `createTemplate`/`updateTemplate`, 현장·조직 override는 `updateMeta`
         * (둘 다 `firstSlotOnlySourceViolationMessage`를 본다).
         */
        const firstSlotOnlyViolating =
          !unassigned &&
          row.assignmentChain.some((s) => !isAssignmentSourceAllowedAtSlot(s.type, idx));
        /** 위반 안내에 이름을 댈 소스들 — 조사(은/는)를 그 마지막 이름의 받침으로 고르려고 한 번 만든다 */
        const firstSlotOnlyLabels = row.assignmentChain
          .filter((s) => !isAssignmentSourceAllowedAtSlot(s.type, idx))
          .map((s) => SOURCE_TYPE_LABELS[s.type])
          .join('·');
        /**
         * 이 편집기에서 담당자를 세울 수 있는가 — 양식은 `특정 멤버`를 박을 수 없고,
         * 첫 칸이 아니면 `작성자`·`위임 대상`도 세울 수 없다(S77).
         */
        const canAssignHere = chainAssignableTypes(whitelist, disableMemberType, idx).length > 0;
        /**
         * 이 칸이 비어 있어 **뒤 칸의 담당자 지정을 지연시키는가**(첫 미지정 칸 + 뒤에 칸이 더 있음).
         * `prefixViolating`과 다르다 — 저건 이미 위반(뒤 칸에 담당자가 있음)이라 경고지만,
         * 이건 아직 위반이 아니고 작성자가 순서대로 채우면 해결된다. 다만 그 수고가 매 보고서마다
         * 반복되므로, 양식에서 채울 수 있는 칸이면 채워 두는 편이 낫다는 것을 알린다.
         */
        const blocksLaterAssignment =
          unassigned && idx === firstUnassignedIndex && idx < lines.length - 1;

        return (
          <div
            key={idx}
            className={`border rounded space-y-2 ${compact ? 'p-2' : 'p-3'} ${
              unassigned
                ? // 미지정 칸: 점선 + amber로 "채워지지 않은 칸"임을 목록에서도 구분
                  // (작성 페이지 편집 모달과 같은 시각 규약).
                  'border-dashed border-amber-300 bg-amber-50'
                : 'border-gray-200'
            }`}
          >
            <div className="flex items-center gap-2">
              <span
                className={
                  compact
                    ? 'text-xs font-medium text-gray-500 shrink-0'
                    : 'text-xs font-medium text-gray-500 w-16'
                }
              >
                Lv.{row.level}
              </span>
              <input
                type="text"
                value={row.label}
                onChange={(e) => updateLabel(idx, e.target.value)}
                placeholder="라벨 (예: 담당자 결재)"
                aria-label={`${row.level}단계 라벨`}
                className="border border-gray-300 rounded px-2 py-1 text-sm flex-1 min-w-0"
              />
              {!compact && (
                <select
                  value={row.locked ? 'true' : 'false'}
                  onChange={(e) => updateLocked(idx, e.target.value === 'true')}
                  aria-label={`${row.level}단계 칸 구조`}
                  className="border border-gray-300 rounded px-2 py-1 text-xs"
                >
                  <option value="true">칸 고정 (조직·현장 변경 불가)</option>
                  <option value="false">칸 편집 허용 (하위 변경 가능)</option>
                </select>
              )}
              <button
                type="button"
                onClick={() => removeRow(idx)}
                className="text-xs text-red-500 hover:text-red-700 px-1 shrink-0"
              >
                삭제
              </button>
            </div>
            {compact && (
              <select
                value={row.locked ? 'true' : 'false'}
                onChange={(e) => updateLocked(idx, e.target.value === 'true')}
                aria-label={`${row.level}단계 칸 구조`}
                className="border border-gray-300 rounded px-2 py-1 text-xs w-full min-w-0"
              >
                <option value="true">칸 고정 (조직·현장 변경 불가)</option>
                <option value="false">칸 편집 허용 (하위 변경 가능)</option>
              </select>
            )}

            <div className={compact ? 'pl-0' : 'pl-16'}>
              {unassigned ? (
                /* 담당자 미지정 칸 — 칸은 있으나 담당자가 없다(= 종이 결재).
                   "실수로 빈 것"과 구별되도록 배지 + 사유를 함께 보여준다. 종전에는 체인이
                   비면 그냥 빈 목록이라 의도인지 실수인지 화면만으로 알 수 없었다. */
                <div className="space-y-1.5">
                  <div className={compact ? 'space-y-1' : 'flex items-center gap-2'}>
                    <Badge label={UNASSIGNED_BADGE_LABEL} color="orange" />
                    <span className="text-xs text-gray-500 block">{UNASSIGNED_HINT}</span>
                  </div>
                  {/* 제한만 걸고 담당자를 세웠다고 오해하기 쉬운 자리다 — 두 축이 한 칸 안에
                      위아래로 붙어 있어 "체크했으니 지정됐겠지"로 읽힌다. 상태를 명시한다. */}
                  {whitelist && (
                    <span className="text-xs text-gray-500 block">
                      아래 소스 제한({whitelist.map((t) => SOURCE_TYPE_LABELS[t]).join('·')})은 걸려
                      있지만, 담당자는 아직 비어 있습니다.
                    </span>
                  )}
                  {/* 규칙 위반은 아니므로 경고(amber)가 아니라 중립 안내다. 알려줄 곳은 **이 칸**
                      이지 뒤 칸이 아니다 — 작성자가 막히는 원인이 여기이고, 뒤 칸에 띄우면
                      저작자가 멀쩡한 칸을 건드리게 된다. */}
                  {blocksLaterAssignment && (
                    <span className="text-xs text-gray-500 block">
                      이 칸을 비워 두면 작성자가 뒤 칸의 담당자를 지정하기 전에 이 칸부터 채워야
                      합니다(담당자는 앞에서부터 차례로).
                    </span>
                  )}
                  {canAssignHere ? (
                    <button
                      type="button"
                      onClick={() => assignAssignee(idx)}
                      className="text-xs text-red-600 hover:text-red-800"
                    >
                      + 담당자 지정
                    </button>
                  ) : (
                    /* 허용된 소스가 `특정 멤버`뿐인 양식 칸 — whitelist로는 완전히 유효한
                       구성(작성자가 조직 멤버를 골라 세운다)이지만 양식이 개인을 박을 수는
                       없다. 버튼을 눌러도 아무 일이 없는 대신 어디서 정해지는지 알린다. */
                    <span className="text-xs text-gray-500 block">
                      허용된 소스가 양식에서 세울 수 없는 종류뿐입니다 — 담당자는 보고서를 만들
                      때 지정합니다.
                    </span>
                  )}
                </div>
              ) : (
                <>
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <p className="text-xs text-gray-500">결재자 소스 체인</p>
                    <button
                      type="button"
                      onClick={() => unsetAssignee(idx)}
                      className="text-xs text-gray-500 hover:text-gray-700 underline shrink-0"
                    >
                      담당자 해제
                    </button>
                  </div>
                  <AssignmentChainEditor
                    chain={row.assignmentChain}
                    onChange={(chain) => updateChain(idx, chain)}
                    disableMemberType={disableMemberType}
                    allowedTypes={whitelist}
                    compact={compact}
                    slotIndex={idx}
                  />
                  {whitelistViolating && (
                    <p className="mt-1 text-xs text-amber-700 bg-amber-100 border border-amber-200 rounded px-2 py-1">
                      허용 목록에 없는 소스가 기본값입니다. 현장이 손대지 않으면 이 값이 그대로
                      쓰이므로 아래 제한이 무의미해집니다.
                    </p>
                  )}
                </>
              )}

              {prefixViolating && (
                <p className="mt-2 text-xs text-amber-700 bg-amber-100 border border-amber-200 rounded px-2 py-1">
                  앞 칸이 담당자 미지정이라 이대로 두면 작성자가 제출할 때 막힙니다. 담당자는 앞
                  칸부터 차례로 지정해야 합니다.
                </p>
              )}

              {firstSlotOnlyViolating && (
                <p className="mt-2 text-xs text-amber-700 bg-amber-100 border border-amber-200 rounded px-2 py-1">
                  <strong>{firstSlotOnlyLabels}</strong>
                  {topicParticleOf(firstSlotOnlyLabels)} 첫 번째 결재란에서만 쓸 수 있어 이대로는 <strong>저장이 거부됩니다</strong>.
                  뒤 결재란에 두면 작성·제출과 동시에 자동으로 채워져 제3자 결재를 한 번도 거치지
                  않습니다. 역할이나 특정 멤버로 바꾸거나, 담당자를 비워 두세요.
                </p>
              )}

              {/* 담당자 소스 제한(whitelist) — `locked`(칸 구조 권한)와 **축이 다르다**.
                  locked는 칸을 지우거나 옮기지 못하게 하고, 이 목록은 그 칸에 **누구를 담당자로
                  세울 수 있는지**를 좁힌다. 섞이지 않도록 라벨에 축을 적어 둔다.
                  미지정 칸에도 걸 수 있다 — 나중에 채울 때의 제한이기 때문. */}
              <div className="mt-2 pt-2 border-t border-gray-100">
                <p className="text-xs text-gray-500 mb-1">
                  지정 가능한 담당자 소스 종류
                  <span className="ml-1 text-gray-400">
                    (선택 없음 = 제한 없음{whitelist ? '' : ' — 현재 제한 없음'})
                  </span>
                </p>
                <div className="flex flex-wrap gap-x-3 gap-y-1">
                  {/* ⚠️ `disableMemberType`을 여기 걸면 안 된다 — 그 prop은 **체인**의 제약
                      (양식이 특정 개인 memberId를 박으면 조직·현장이 바뀔 때 깨진다)이고,
                      이 체크는 반대로 "작성자가 조직 멤버를 골라 세워도 된다"는 **허용**이다.
                      막아 두면 제한을 하나라도 켜는 순간 특정 멤버가 강제 배제되어
                      "역할 또는 담당자 지목" 같은 구성을 표현할 수 없다. */}
                  {SOURCE_TYPES.map((t) => {
                    const checked = whitelist?.includes(t) ?? false;
                    /**
                     * S77: 첫 칸이 아니면 `작성자`·`위임 대상`은 애초에 세울 수 없으므로 **새로
                     * 허용하는 것**을 막는다. 허용해 봐야 아무도 그 소스를 못 고르고, 그것만
                     * 허용하면 **누구도 담당자를 세울 수 없는 죽은 칸**이 된다.
                     *
                     * ⚠️ 이미 켜져 있으면 잠그지 않는다 — 잠그면 기존 저장분(옛 기본값
                     * `[{delegatedTo}]` 시절 양식)을 **끄지도 못하는 막다른 길**이 된다.
                     */
                    const slotBlocked = !isAssignmentSourceAllowedAtSlot(t, idx) && !checked;
                    const slotBlockedReason = slotBlocked
                      ? '첫 결재란에서만 고를 수 있습니다 — 뒤 칸에 두면 작성·제출과 동시에 채워져 제3자 결재를 거치지 않습니다.'
                      : undefined;
                    return (
                      // 사유는 **호버(`title`)로만** 준다 — 2번 칸부터는 늘 같은 둘이 회색이라
                      // 라벨에 적으면 매 행에 두 번씩 반복돼 목록이 사유 텍스트로 뒤덮인다.
                      //
                      // ⚠️ 사유를 `aria-label`에 넣지 않는다. 접근성 **이름**은 컨트롤을
                      // *식별*하는 자리이고(테스트도 그 이름으로 찾는다), 사유는 *설명*이라
                      // `title` 쪽이다. 이름에 상태를 섞으면 표시 방식이 바뀔 때마다 이름이
                      // 흔들려 조회가 깨진다. 글자와 체크박스 양쪽에 붙여 어디에 올려도 뜬다.
                      <label
                        key={t}
                        title={slotBlockedReason}
                        className={`flex items-center gap-1 text-xs ${
                          slotBlocked ? 'text-gray-400' : 'text-gray-600'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          disabled={slotBlocked}
                          onChange={() => toggleWhitelist(idx, t)}
                          title={slotBlockedReason}
                          aria-label={`${row.level}단계 담당자 소스 허용: ${SOURCE_TYPE_LABELS[t]}`}
                        />
                        {SOURCE_TYPE_LABELS[t]}
                      </label>
                    );
                  })}
                </div>
                <p className="text-[11px] text-gray-400 mt-1">
                  종류를 <strong>좁히기만</strong> 합니다 — 체크해도 담당자가 지정되지는 않습니다.
                  담당자를 비우는 것은 이 제한과 무관하게 항상 허용됩니다.
                </p>
              </div>
            </div>
          </div>
        );
      })}
      {lines.length === 0 && (
        <p className="text-sm text-gray-400 text-center py-2">결재라인 없음 (단계 추가 필요)</p>
      )}
      <button
        type="button"
        onClick={addRow}
        className="text-sm text-red-600 hover:text-red-800 flex items-center gap-1"
      >
        + 결재 단계 추가
      </button>
    </div>
  );
}
